/**
 * Measurement units for ingredient stock.
 *
 * Every unit belongs to a dimension and carries a factor to that dimension's base
 * unit (gram, millilitre, piece). Stock is *held* in the base unit as an integer of
 * thousandths, so 12.5 kg is stored as 12_500_000 milli-grams. Integers again: a
 * recipe that consumes 2.5 g of yeast a hundred times a day must not drift.
 */
export const UNITS = [
    // Mass — base: gram
    { code: 'g', name: 'gram', plural: 'grams', dimension: 'mass', factor: 1, precision: 0 },
    { code: 'kg', name: 'kilogram', plural: 'kilograms', dimension: 'mass', factor: 1000, precision: 3 },
    { code: 'mg', name: 'milligram', plural: 'milligrams', dimension: 'mass', factor: 0.001, precision: 0 },
    { code: 'oz', name: 'ounce', plural: 'ounces', dimension: 'mass', factor: 28.349523125, precision: 2 },
    { code: 'lb', name: 'pound', plural: 'pounds', dimension: 'mass', factor: 453.59237, precision: 2 },
    { code: 'sack25', name: '25 kg sack', plural: '25 kg sacks', dimension: 'mass', factor: 25000, precision: 2 },
    { code: 'sack50', name: '50 kg sack', plural: '50 kg sacks', dimension: 'mass', factor: 50000, precision: 2 },
    // Volume — base: millilitre
    { code: 'ml', name: 'millilitre', plural: 'millilitres', dimension: 'volume', factor: 1, precision: 0 },
    { code: 'l', name: 'litre', plural: 'litres', dimension: 'volume', factor: 1000, precision: 3 },
    { code: 'tsp', name: 'teaspoon', plural: 'teaspoons', dimension: 'volume', factor: 4.92892159375, precision: 2 },
    { code: 'tbsp', name: 'tablespoon', plural: 'tablespoons', dimension: 'volume', factor: 14.78676478125, precision: 2 },
    { code: 'cup', name: 'cup', plural: 'cups', dimension: 'volume', factor: 236.5882365, precision: 2 },
    { code: 'gal', name: 'gallon', plural: 'gallons', dimension: 'volume', factor: 3785.411784, precision: 2 },
    // Count — base: piece
    { code: 'pc', name: 'piece', plural: 'pieces', dimension: 'count', factor: 1, precision: 0 },
    { code: 'dozen', name: 'dozen', plural: 'dozens', dimension: 'count', factor: 12, precision: 2 },
    { code: 'tray30', name: 'tray of 30', plural: 'trays of 30', dimension: 'count', factor: 30, precision: 2 },
    { code: 'box', name: 'box', plural: 'boxes', dimension: 'count', factor: 1, precision: 0 },
    { code: 'pack', name: 'pack', plural: 'packs', dimension: 'count', factor: 1, precision: 0 },
    { code: 'bottle', name: 'bottle', plural: 'bottles', dimension: 'count', factor: 1, precision: 0 },
];
const UNIT_BY_CODE = new Map(UNITS.map((u) => [u.code, u]));
export function getUnit(code) {
    const unit = UNIT_BY_CODE.get(code);
    if (!unit)
        throw new Error(`Unknown unit: ${code}`);
    return unit;
}
export function unitsForDimension(dimension) {
    return UNITS.filter((u) => u.dimension === dimension);
}
/** Scale factor between the stored integer and one base unit. */
const BASE_SCALE = 1000;
/** Convert a human quantity in `unitCode` into the stored integer base quantity. */
export function toBase(qty, unitCode) {
    return Math.round(qty * getUnit(unitCode).factor * BASE_SCALE);
}
/** Convert a stored base quantity back into `unitCode`, as a decimal number. */
export function fromBase(qty, unitCode) {
    return qty / (getUnit(unitCode).factor * BASE_SCALE);
}
/**
 * Two units can only be converted between when they share a dimension. Attempting
 * to price 2 litres of flour is a data-entry mistake, and should surface as one.
 */
export function areCompatible(a, b) {
    return getUnit(a).dimension === getUnit(b).dimension;
}
export function convert(qty, from, to) {
    if (!areCompatible(from, to)) {
        throw new Error(`Cannot convert ${from} to ${to} — different dimensions`);
    }
    return (qty * getUnit(from).factor) / getUnit(to).factor;
}
/** Format a stored base quantity for display in its stocking unit. */
export function formatQty(qty, unitCode, options = {}) {
    const unit = getUnit(unitCode);
    const value = fromBase(qty, unitCode);
    const rounded = Number(value.toFixed(unit.precision));
    const number = rounded.toLocaleString('en-PH', { maximumFractionDigits: unit.precision });
    if (!options.long)
        return `${number} ${unit.code}`;
    return `${number} ${Math.abs(rounded) === 1 ? unit.name : unit.plural}`;
}
/**
 * Pick the unit a human would naturally read this quantity in.
 * 2 400 g of flour reads better as "2.4 kg"; 300 g stays "300 g".
 */
export function humanizeQty(qty, stockUnitCode) {
    const unit = getUnit(stockUnitCode);
    const candidates = unitsForDimension(unit.dimension)
        .filter((u) => u.factor <= 1000 && ['g', 'kg', 'ml', 'l', 'pc'].includes(u.code))
        .sort((a, b) => b.factor - a.factor);
    const absolute = Math.abs(qty);
    for (const candidate of candidates) {
        if (absolute >= candidate.factor * BASE_SCALE)
            return formatQty(qty, candidate.code);
    }
    return formatQty(qty, candidates.at(-1)?.code ?? stockUnitCode);
}
//# sourceMappingURL=units.js.map